import * as React from 'react'
import { Animated, Text, TouchableOpacity, View, ViewProps } from 'react-native'
import styled from '@theme/styled-components'

import Icon from '@components/icon/Icons'
import SiteIcon from '@components/icon/SiteIcons'
import AlertButton, { BUTTON_HEIGHT } from '@components/button/AlertButton'

import * as SiteStore from '@store/site/site'
import { store } from '@utils/useSiteChangeHandler'

import useI18n from '@i18n/useI18n'
import useReducer from '@store/useReducer'
import useConnectivity from '@utils/useConnectivity'
import useBreakpoint from '@utils/useBreakpoints'

import theme from '@theme/Theme'

const MAIN_VERTICAL_PADDING = 16
const ANIM_DURATION = 500
const SMALL_HEADER_HEIGHT = 65

interface Props extends ViewProps {
  hasScrolled: boolean
  onBack?: () => void
  onMapPressed: () => void
  actionButton?: { label: string; onClick: () => void }
  validationButton?: { label: string; onClick: () => void }
}

const SiteHeader = ({ hasScrolled, onBack, onMapPressed, actionButton, validationButton, ...rest }: Props) => {
  const i18n = useI18n()

  const site = useReducer(SiteStore.store, (s) => s.site!)
  const lastUpdate = useReducer(store, (s) => s.lastUpdate)

  const [connected, loading] = useConnectivity()

  const [{ width }] = useBreakpoint()
  const [initHeight, setInitHeight] = React.useState(0)
  const [height, setHeight] = React.useState(0)

  const nameOpacityAnim = React.useRef(new Animated.Value(0)).current
  const contentOpacityAnim = React.useRef(new Animated.Value(1)).current
  const heightAnim = React.useRef(new Animated.Value(0)).current

  const actionsCount = [actionButton, validationButton].filter(Boolean).length

  React.useEffect(() => {
    // Small site name appears

    Animated.parallel([
      Animated.timing(nameOpacityAnim, {
        toValue: hasScrolled ? 1 : 0,
        duration: ANIM_DURATION,
        useNativeDriver: true,
      }),

      Animated.timing(contentOpacityAnim, {
        toValue: hasScrolled ? 0 : 1,
        duration: ANIM_DURATION / 2,
        useNativeDriver: true,
      }),
      Animated.timing(heightAnim, {
        toValue: hasScrolled ? SMALL_HEADER_HEIGHT : initHeight,
        duration: ANIM_DURATION,
        useNativeDriver: false,
      }),
    ]).start()
  }, [hasScrolled, initHeight])

  const fadeAndScaleAnim: any = {
    opacity: contentOpacityAnim,
    transform: [{ scaleY: contentOpacityAnim }],
  }

  return (
    <GlobalContainer hasScrolled={hasScrolled} hasActions={actionsCount} {...rest}>
      <SafeContainer>
        {!connected && !loading && (
          <OfflineContainer>
            <Icon name="connectivity" size={24} color={theme.colors.background} />
            <OfflineInfos>
              <OfflineTitle>{i18n.t('screens.site.offline.title')}</OfflineTitle>
              <OfflineDescription>
                {i18n.t('screens.site.offline.description')}
                {!!lastUpdate && (
                  <OfflineDate>{i18n.t('screens.site.offline.date', { date: new Date(lastUpdate) })}</OfflineDate>
                )}
              </OfflineDescription>
            </OfflineInfos>
          </OfflineContainer>
        )}
        <Container
          hasScrolled={hasScrolled}
          style={initHeight > 0 ? { height: heightAnim } : undefined}
          onLayout={(e) => {
            if (initHeight === 0) {
              heightAnim.setValue(e.nativeEvent.layout.height)
              setInitHeight(e.nativeEvent.layout.height)
            }
            setHeight(e.nativeEvent.layout.height)
          }}>
          <SiteIconContainer style={{ opacity: contentOpacityAnim }} height={height} width={width}>
            <SiteIcon name={site.codeLieu as SiteIconName} height={400} color={theme.colors.primaryDark} />
          </SiteIconContainer>
          <TopContainer>
            {!!onBack && (
              <BackButton activeOpacity={0.9} onPress={onBack}>
                <Icon name="arrow-left" size={35} color={theme.colors.background} />
              </BackButton>
            )}
            <SmallName style={{ opacity: nameOpacityAnim }} numberOfLines={1} ellipsizeMode="tail">
              {i18n.t(`screens.site.sites.${site.codeLieu}`)}
            </SmallName>
            {/* TODO : conditionner à la présence d'un plan */}
            {true && (
              <MapButton activeOpacity={0.9} onPress={onMapPressed}>
                <Icon name="map" size={20} color={theme.colors.primary} />
              </MapButton>
            )}
          </TopContainer>

          <Name style={fadeAndScaleAnim} numberOfLines={2} ellipsizeMode="tail">
            {i18n.t(`screens.site.sites.${site.codeLieu}`)}
          </Name>
        </Container>
      </SafeContainer>
      {(!!actionButton || !!validationButton) && (
        <AlertButtonContainer hasScrolled={hasScrolled} width={width} style={fadeAndScaleAnim}>
          {!!actionButton && (
            <ButtonBackground isTop={!!validationButton}>
              <AlertButton label={actionButton.label} onClick={actionButton.onClick} />
            </ButtonBackground>
          )}

          {!!validationButton && (
            <ButtonBackground>
              <AlertButton style="urgent" label={validationButton.label} onClick={validationButton.onClick} />
            </ButtonBackground>
          )}
        </AlertButtonContainer>
      )}
    </GlobalContainer>
  )
}

export default SiteHeader
const GlobalContainer = styled(View)<{ hasScrolled: boolean; hasActions: number }>`
  ${(props) => !props.hasScrolled && `padding-bottom: ${BUTTON_HEIGHT * (props.hasActions - 1) + BUTTON_HEIGHT / 2}px;`}
`
const SafeContainer = styled(View)`
  z-index: 2;
  background-color: ${(props) => props.theme.colors.primary};
  overflow: hidden;
  padding-top: ${(props) => props.theme.insets.top}px;
`
const Container = styled(Animated.View)<{ hasScrolled: boolean }>`
  z-index: 2;
  background-color: ${(props) => props.theme.colors.primary};
  ${(props) => !props.hasScrolled && `margin-bottom: ${BUTTON_HEIGHT / 2 + MAIN_VERTICAL_PADDING}px;`}
`
const OfflineContainer = styled(View)`
  padding: 16px;
  flex-direction: row;
  align-items: center;
  background-color: ${(props) => props.theme.colors.negate};
  z-index: 3;
`
const OfflineInfos = styled(View)`
  margin-left: 16px;
`
const OfflineTitle = styled(Text)`
  ${(props) => props.theme.fonts.Subheadline};
  color: ${(props) => props.theme.colors.background};
  margin-bottom: 4px;
`
const OfflineDescription = styled(Text)`
  ${(props) => props.theme.fonts.Caption1};
  color: ${(props) => props.theme.colors.background};
`
const OfflineDate = styled(Text)`
  ${(props) => props.theme.fonts.Subheadline};
  font-size: ${(props) => props.theme.fonts.Caption1.fontSize};
  color: ${(props) => props.theme.colors.background};
`
const TopContainer = styled(View)`
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding: ${MAIN_VERTICAL_PADDING}px;
`
const BackButton = styled(TouchableOpacity)`
  flex-direction: row;
  align-items: center;
`
const Name = styled(Animated.Text)`
  ${(props) => props.theme.fonts.Title1};
  color: ${(props) => props.theme.colors.background};
  text-align: center;
  padding: ${MAIN_VERTICAL_PADDING}px;
`
const SmallName = styled(Animated.Text)`
  ${(props) => props.theme.fonts.Headline};
  color: ${(props) => props.theme.colors.background};
  text-align: center;
  flex: 1;
`
const MapButton = styled(TouchableOpacity)`
  height: 32px;
  width: 32px;
  border-radius: 36px;
  align-items: center;
  justify-content: center;
  background-color: ${(props) => props.theme.colors.background};
`
const AlertButtonContainer = styled(Animated.View)<{ width: number; hasScrolled: boolean }>`
  z-index: ${(props) => (props.hasScrolled ? 0 : 2)};
  padding: ${MAIN_VERTICAL_PADDING}px;
  padding-bottom: 0;
  width: ${(props) => props.width}px;
  position: absolute;
  left: 0;
  bottom: 0;
`
const SiteIconContainer = styled(Animated.View)<{ height: number; width: number }>`
  z-index: -1;
  position: absolute;
  bottom: -${BUTTON_HEIGHT / 2 + MAIN_VERTICAL_PADDING}px;
  left: 0;
  height: ${(props) => props.height + 100}px;
  width: ${(props) => props.width}px;
  justify-content: center;
  align-items: center;
`
const ButtonBackground = styled(View)<{ isTop?: boolean }>`
  border-radius: 10px;
  background-color: ${(props) => props.theme.colors.primary};
  ${(props) => props.isTop && `margin-bottom: 8px;`}
`
